import { Component, signal, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { NotificationCenterService } from '../../../core/services/notification-center.service';
import { TranslationService } from '../../../core/services/i18n/translation.service';
import { roleLabel } from '../../../core/utils/role-label';
import { LanguageSwitcherComponent } from '../language-switcher/language-switcher.component';

interface AdminNavItem {
  labelKey: string;
  icon: string;
  route: string;
  /** The accountant may open it too (route data.accounting). */
  accounting?: boolean;
  /** Only super_admin, administrator and accountant, whatever else the user is (route data.financeOnly). */
  financeOnly?: boolean;
  /** Only the super admin may open it (route guarded by superAdminGuard). */
  superAdmin?: boolean;
  /** Only a user with executive access (route guarded by executiveGuard). */
  executive?: boolean;
}

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, LanguageSwitcherComponent],
  templateUrl: './admin-layout.component.html',
  host: { class: 'block' },
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
  showUserMenu = signal(false);
  sidebarCollapsed = signal(false);
  mobileMenuOpen = signal(false);

  adminNavItems: AdminNavItem[] = [
    { labelKey: 'adminNav.dashboard', icon: 'fas fa-tachometer-alt', route: '/admin/dashboard' },
    {
      labelKey: 'nav.ceo',
      icon: 'fas fa-chart-line',
      route: '/executive-dashboard',
      executive: true,
    },
    { labelKey: 'adminNav.users', icon: 'fas fa-users', route: '/admin/users' },
    { labelKey: 'adminNav.roles', icon: 'fas fa-user-shield', route: '/admin/roles' },
    {
      labelKey: 'adminNav.emailTemplates',
      icon: 'fas fa-envelope',
      route: '/admin/email-templates',
    },
    {
      labelKey: 'adminNav.connectedMailboxes',
      icon: 'fas fa-inbox',
      route: '/admin/gmail-accounts',
    },
    {
      labelKey: 'adminNav.companySettings',
      icon: 'fas fa-building',
      route: '/admin/company-settings',
      accounting: true,
    },
    {
      labelKey: 'adminNav.documentRules',
      icon: 'fas fa-file-contract',
      route: '/admin/document-rules',
    },
    {
      labelKey: 'adminNav.invoiceTerms',
      icon: 'fas fa-file-invoice',
      route: '/admin/invoice-terms',
      accounting: true,
    },
    {
      labelKey: 'debtCollection.title',
      icon: 'fas fa-gavel',
      route: '/admin/debt-collection/cases',
      financeOnly: true,
    },
    {
      labelKey: 'adminNav.poRequiredDocuments',
      icon: 'fas fa-clipboard-check',
      route: '/admin/po-required-documents',
    },
    {
      labelKey: 'adminNav.productAttributes',
      icon: 'fas fa-tags',
      route: '/admin/product-attributes',
    },
    { labelKey: 'adminNav.ports', icon: 'fas fa-anchor', route: '/admin/ports' },
    {
      labelKey: 'adminNav.systemSettings',
      icon: 'fas fa-cog',
      route: '/admin/settings',
      superAdmin: true,
    },
    {
      labelKey: 'adminNav.auditLogs',
      icon: 'fas fa-file-alt',
      route: '/admin/audit-logs',
      superAdmin: true,
    },
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    public translationService: TranslationService,
    /** The same unread count as the main navbar's bell */
    public notificationCenter: NotificationCenterService
  ) {}

  // The bell stays live while the signed-in shell is open (card 927); leaving it (sign-out) closes the socket
  ngOnInit(): void {
    this.notificationCenter.connect();
  }

  ngOnDestroy(): void {
    this.notificationCenter.disconnect();
  }

  @HostListener('window:resize')
  onResize(): void {
    // Close mobile menu on desktop resize
    if (window.innerWidth >= 1024) {
      this.mobileMenuOpen.set(false);
    }
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update((value) => !value);
  }

  toggleUserMenu(): void {
    this.showUserMenu.update((value) => !value);
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen.update((value) => !value);
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
  }

  openNotifications(): void {
    this.router.navigate(['/notifications']);
  }

  backToApp(): void {
    this.router.navigate([this.authService.getLandingRoute()]);
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
    this.showUserMenu.set(false);
  }

  /** Super admins see every item, admins all but the super-admin ones, the accountant the accounting pages. */
  get visibleNavItems(): AdminNavItem[] {
    // The same role check as the guard, so a menu item never leads to a refused page
    const finance = this.authService.canManageAccounting();
    return this.adminNavItems.filter((item) => {
      if (item.executive) return this.authService.hasExecutiveAccess();
      if (item.financeOnly) return finance;
      if (this.isSuperAdmin) return true;
      return this.authService.isAdmin() ? !item.superAdmin : !!item.accounting;
    });
  }

  get isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin();
  }

  /** The role in the reader's language; the API sends a code ("super_admin") or "Super_Admin". */
  roleText(): string {
    const user = this.currentUser;
    const role = user?.role_name || user?.role_display;
    return role ? roleLabel(role, this.translationService, user?.role_display || role) : '';
  }

  get currentUser() {
    return this.authService.currentUser();
  }
}
